import React from 'react';
import { Lock } from 'lucide-react';
import { SECTION_TITLES, SECTION_FULL_TITLES, SECTION_ICONS } from './sectionConfig';
import {
  BasicInfoContent, StatusContent, InvoiceContent, UpdatedInvoiceContent,
  WeightAdjustmentContent, BillingContent, PaymentContent
} from './sections';

const SECTION_TITLE_MAP = {
  basic: 'Basic Information', invoice: 'Invoice Details', status: 'Delivery & Status',
  updated_invoice: 'Updated Invoice', weight_adjustment: 'Weight Adjustment',
  billing: 'Billing & Follow-up', payment: 'Payment Tracking',
};

function getCardColors(section, isSectionLocked, isSectionComplete) {
  if (isSectionLocked(section)) {
    return { bg1: '#FDE8E8', bg2: '#FFF5F5', border: '#F08080', hdr1: '#F08080', hdr2: '#F4A0A0', num: '#ffffff', title: '#ffffff' };
  }
  if (isSectionComplete(section)) {
    return { bg1: '#d1fae5', bg2: '#ecfdf5', border: '#34d399', hdr1: '#059669', hdr2: '#34d399', num: '#ffffff', title: '#ffffff' };
  }
  return { bg1: '#fffbeb', bg2: '#ffffff', border: '#fde68a', hdr1: '#facc15', hdr2: '#fde68a', num: '#713f12', title: '#78350f' };
}

function getProgressIconStyle(section, visIdx, desktopActiveSection, isSectionLocked, isSectionComplete, isSectionEnabled) {
  const isLocked = isSectionLocked(section);
  const isComplete = isSectionComplete(section);
  const isEnabled = isSectionEnabled(section);
  const isCurrent = desktopActiveSection === visIdx;
  const style = {};

  if (isLocked) {
    style.background = '#F08080'; style.color = '#ffffff';
    style.boxShadow = isCurrent ? '0 0 0 3px white, 0 0 0 5px #D3D3D3' : '0 0 0 3px #D3D3D3';
  } else if (isComplete) {
    style.background = '#059669'; style.color = '#ffffff';
  } else if (isEnabled) {
    style.background = '#facc15'; style.color = '#713f12';
  } else {
    style.background = '#e2e8f0'; style.color = '#94a3b8';
  }

  if (isCurrent) {
    style.transform = 'scale(1.15)';
    if (!isLocked) {
      style.boxShadow = '0 0 0 3px white, 0 0 0 5px #facc15';
    }
  }
  return style;
}

function getTabStyle(section, visIdx, desktopActiveSection, isSectionLocked, isSectionComplete, isSectionEnabled) {
  const isActive = visIdx === desktopActiveSection;
  const isLocked = isSectionLocked(section);
  const isComplete = isSectionComplete(section);
  const isEnabled = isSectionEnabled(section);
  let bg, textColor, borderColor;

  if (isLocked) {
    if (isActive) {
      bg = '#F08080'; textColor = '#ffffff'; borderColor = '#E06060';
    } else {
      bg = '#FDE8E8'; textColor = '#B91C1C'; borderColor = '#F08080';
    }
  } else if (isActive) {
    if (isComplete) {
      bg = '#059669'; textColor = '#ffffff'; borderColor = '#047857';
    } else {
      bg = '#facc15'; textColor = '#713f12'; borderColor = '#eab308';
    }
  } else if (isComplete) {
    bg = '#d1fae5'; textColor = '#065f46'; borderColor = '#34d399';
  } else if (isEnabled) {
    bg = '#fef3c7'; textColor = '#92400e'; borderColor = '#fde68a';
  } else {
    bg = '#f1f5f9'; textColor = '#94a3b8'; borderColor = '#e2e8f0';
  }

  return {
    background: bg, color: textColor, borderColor,
    fontWeight: isActive ? '700' : undefined,
    boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : undefined,
    cursor: (isEnabled || isLocked) ? 'pointer' : 'not-allowed'
  };
}

export default function DesktopStackedCardsView({ contextValue }) {
  const {
    formData, handleFieldChange, errors, preventWheelChange,
    editingLoad, setErrorDialog,
    ALL_SECTIONS, desktopActiveSection, setDesktopActiveSection,
    isSectionEnabled, isSectionComplete, isSectionLocked, isSectionVisible,
    invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
    invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
    permitFile1, setPermitFile1, permitFile2, setPermitFile2,
    otherBills1, setOtherBills1, otherBills2, setOtherBills2,
    updatedInvoiceFile, setUpdatedInvoiceFile,
    additionInvoiceFile, setAdditionInvoiceFile,
    deductionInvoiceFile, setDeductionInvoiceFile,
    removePermitFile, setRemovePermitFile,
    removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
    removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
    removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
    generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
    handleGenerateInvoiceClick, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
    handleGenerateUpdatedInvoiceClick, handleGenerateAdditionInvoiceClick, handleGenerateDeductionInvoiceClick,
    handleSendEmailClick,
    invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
    manualInvoiceErrors, clearManualInvoiceError,
    peekedNumber, peeking, peekNextInvoiceNumber,
    deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
  } = contextValue;

  // Create onError callback for date validation errors
  const handleDateError = (message, fieldName, fieldId) => {
    if (setErrorDialog) {
      setErrorDialog({ open: true, message, fieldName, fieldId });
    }
  };

  const visibleSections = ALL_SECTIONS.filter(s => isSectionVisible(s));
  const activeSection = visibleSections[desktopActiveSection];

  const renderSectionContent = (section) => {
    switch (section) {
      case 'basic':
        return <BasicInfoContent formData={formData} handleFieldChange={handleFieldChange} isEditMode={!!editingLoad?.load_id} locked={isSectionLocked('basic')} onError={handleDateError} peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber} />;
      case 'invoice':
        return (
          <InvoiceContent
            formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange} editingLoad={editingLoad}
            invoiceFile={invoiceFile} setInvoiceFile={setInvoiceFile}
            permitFile={permitFile} setPermitFile={setPermitFile}
            otherBills={otherBills} setOtherBills={setOtherBills}
            removePermitFile={removePermitFile} setRemovePermitFile={setRemovePermitFile}
            generating={generating} handleGenerateInvoiceClick={handleGenerateInvoiceClick}
            handleSendEmailClick={handleSendEmailClick}
            invoiceFile1={invoiceFile1} setInvoiceFile1={setInvoiceFile1}
            invoiceFile2={invoiceFile2} setInvoiceFile2={setInvoiceFile2}
            permitFile1={permitFile1} setPermitFile1={setPermitFile1}
            permitFile2={permitFile2} setPermitFile2={setPermitFile2}
            otherBills1={otherBills1} setOtherBills1={setOtherBills1}
            otherBills2={otherBills2} setOtherBills2={setOtherBills2}
            generating1={generating1} generating2={generating2}
            handleGenerateInvoice1Click={handleGenerateInvoice1Click}
            handleGenerateInvoice2Click={handleGenerateInvoice2Click}
            invoiceFile1Ref={invoiceFile1Ref} invoiceFile2Ref={invoiceFile2Ref}
            permitFile1Ref={permitFile1Ref} permitFile2Ref={permitFile2Ref}
            locked={isSectionLocked('invoice')}
            manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
            peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
          />
        );
      case 'status':
        return (
          <StatusContent 
            formData={formData} 
            handleFieldChange={handleFieldChange} 
            locked={isSectionLocked('status')}
            deliveryAttachments={deliveryAttachments}
            setDeliveryAttachments={setDeliveryAttachments}
            existingDeliveryFiles={existingDeliveryFiles}
            onRemoveExistingFile={(index) => setExistingDeliveryFiles(prev => prev.filter((_, i) => i !== index))}
          />
        );
      case 'updated_invoice':
        return (
          <UpdatedInvoiceContent
            formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
            updatedInvoiceFile={updatedInvoiceFile} setUpdatedInvoiceFile={setUpdatedInvoiceFile}
            removeUpdatedInvoiceFile={removeUpdatedInvoiceFile} setRemoveUpdatedInvoiceFile={setRemoveUpdatedInvoiceFile}
            editingLoad={editingLoad}
            generatingUpdated={generatingUpdated} handleGenerateUpdatedInvoiceClick={handleGenerateUpdatedInvoiceClick}
            handleSendEmailClick={handleSendEmailClick}
            locked={isSectionLocked('updated_invoice')}
          />
        );
      case 'weight_adjustment':
        return (
          <WeightAdjustmentContent
            formData={formData} handleFieldChange={handleFieldChange} errors={errors} preventWheelChange={preventWheelChange} editingLoad={editingLoad}
            additionInvoiceFile={additionInvoiceFile} setAdditionInvoiceFile={setAdditionInvoiceFile}
            deductionInvoiceFile={deductionInvoiceFile} setDeductionInvoiceFile={setDeductionInvoiceFile}
            removeAdditionInvoiceFile={removeAdditionInvoiceFile} setRemoveAdditionInvoiceFile={setRemoveAdditionInvoiceFile}
            removeDeductionInvoiceFile={removeDeductionInvoiceFile} setRemoveDeductionInvoiceFile={setRemoveDeductionInvoiceFile}
            generatingAddition={generatingAddition} handleGenerateAdditionInvoiceClick={handleGenerateAdditionInvoiceClick}
            generatingDeduction={generatingDeduction} handleGenerateDeductionInvoiceClick={handleGenerateDeductionInvoiceClick}
            handleSendEmailClick={handleSendEmailClick}
            locked={isSectionLocked('weight_adjustment')}
          />
        );
      case 'billing':
        return <BillingContent formData={formData} handleFieldChange={handleFieldChange} errors={errors} preventWheelChange={preventWheelChange} locked={isSectionLocked('billing')} />;
      case 'payment':
        return <PaymentContent formData={formData} handleFieldChange={handleFieldChange} errors={errors} locked={isSectionLocked('payment')} />;
      default:
        return null;
    }
  };

  return (
    <div className="stacked-cards-container">
      {/* Desktop Progress Bar */}
      <div className="desktop-progress-bar">
        {visibleSections.map((section, idx, arr) => {
          const visIdx = visibleSections.indexOf(section);
          const isEnabled = isSectionEnabled(section);
          const isComplete = isSectionComplete(section);
          const isLocked = isSectionLocked(section);
          const IconComp = SECTION_ICONS[section];
          const iconStyle = getProgressIconStyle(section, visIdx, desktopActiveSection, isSectionLocked, isSectionComplete, isSectionEnabled);

          return (
            <React.Fragment key={section}>
              <button
                type="button"
                onClick={() => isEnabled && setDesktopActiveSection(visIdx)}
                disabled={!isEnabled}
                className="progress-icon-btn progress-icon-enter"
                style={{ ...iconStyle, animationDelay: `${idx * 0.05}s` }}
                title={SECTION_FULL_TITLES[section]}
                data-testid={`progress-icon-${section}`}
              >
                {isLocked ? <Lock className="w-4 h-4" /> : <IconComp className="w-4 h-4" />}
              </button>
              {idx < arr.length - 1 && (
                <div className="progress-connector" style={isComplete ? { background: '#059669' } : {}} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Card + Right Tabs Layout */}
      <div className="card-tabs-layout">
        {/* Active section card - left side */}
        {activeSection && (() => {
          const sectionNumber = String(desktopActiveSection + 1).padStart(2, '0');
          const cc = getCardColors(activeSection, isSectionLocked, isSectionComplete);
          return (
            <div className="active-section-card" key={`active-${activeSection}`} style={{ background: `linear-gradient(135deg, ${cc.bg1} 0%, ${cc.bg2} 100%)`, borderColor: cc.border }}>
              <div className="active-card-header" style={{ background: `linear-gradient(135deg, ${cc.hdr1} 0%, ${cc.hdr2} 100%)`, borderColor: cc.border }}>
                <div className="active-card-number" style={{ color: cc.num }}>{sectionNumber}</div>
                <h3 className="active-card-title" style={{ color: cc.title }}>{SECTION_TITLE_MAP[activeSection]}</h3>
              </div>
              <div className="active-card-content section-content-enter field-stagger" key={`content-${activeSection}`}>
                {renderSectionContent(activeSection)}
              </div>
            </div>
          );
        })()}

        {/* Right-side cascading tabs */}
        <div className="right-tabs-area">
          {visibleSections.map((section, visIdx) => {
            const isActive = visIdx === desktopActiveSection;
            const isEnabled = isSectionEnabled(section);
            const isComplete = isSectionComplete(section);
            const isLocked = isSectionLocked(section);
            const tabStyle = getTabStyle(section, visIdx, desktopActiveSection, isSectionLocked, isSectionComplete, isSectionEnabled);
            const TabIcon = SECTION_ICONS[section];
            
            // Check if this tab should blink (next tab after current section)
            // Blink if: it's the next tab, it's enabled, not active, not locked
            const isNextTab = visIdx === desktopActiveSection + 1;
            const shouldBlink = isNextTab && isEnabled && !isActive && !isLocked && !isComplete;

            return (
              <div
                key={section}
                className={`right-tab ${(!isEnabled && !isLocked) ? 'tab-disabled' : ''} ${isLocked ? 'tab-locked' : ''} ${isActive ? 'tab-active' : ''} ${shouldBlink ? 'tab-blink' : ''}`}
                style={{ 
                  ...tabStyle, 
                  animation: shouldBlink 
                    ? undefined  // Let CSS handle the blink animation
                    : `fieldSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) ${visIdx * 0.06}s forwards`,
                  opacity: shouldBlink ? 1 : 0 
                }}
                onClick={() => (isEnabled || isLocked) && setDesktopActiveSection(visIdx)}
                data-testid={`right-tab-${section}`}
              >
                {isLocked ? <Lock style={{ color: tabStyle.color, width: 16, height: 16 }} /> : <TabIcon style={{ color: tabStyle.color }} />}
                <span>{SECTION_TITLES[section]}</span>
                {isActive && <span style={{ marginLeft: 'auto', fontSize: '10px' }}>&#9654;</span>}
                {!isActive && !isLocked && isComplete && <span className="tab-check" style={{ color: '#059669' }}>&#10003;</span>}
                {isLocked && !isActive && <Lock style={{ marginLeft: 'auto', color: tabStyle.color, width: 14, height: 14 }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
